import React, { useMemo } from "react";

import { Empty, Input } from "antd";
import Highlighter from "react-highlight-words";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../Common/UIComponents/StyledTooltip";
import UnlockTestsPanel from "./UnlockTestsPanel";

import getTestName, { buildTestTooltip, groupTestsByLevel } from "./helpers";

import { ReactComponent as SearchIcon } from "../../../assets/icons/Sample Submission Redesign/search-line.svg";

export function MyTestsColumn({
  customerTests = [],
  search = "",
  onSearch,
  onSearchEnter,
  loading = false,
  toolbarExtra = null,
}) {
  const searched = search.trim();
  const groupedTests = useMemo(
    () => groupTestsByLevel(customerTests),
    [customerTests],
  );

  return (
    <section className="TestDirectory__MyTestsPane">
      <div className="TestDirectory__MyTestsHeader">
        <div className="TestDirectory__MyTestsTitleGroup">
          <span className="TestDirectory__MyTestsTitleBar" aria-hidden />
          <h3 className="TestDirectory__MyTestsTitle">My Tests</h3>
        </div>
        <div className="TestDirectory__MyTestsHeaderActions">
          <Input
            allowClear
            value={search}
            onChange={(e) => onSearch?.(e.target.value)}
            onClear={() => onSearch?.("")}
            onPressEnter={(e) => onSearchEnter?.(e.target.value)}
            placeholder="Search Tests"
            className="TestDirectory__SearchInput TestDirectory__MyTestsSearch"
            prefix={<SearchIcon aria-hidden className="TestDirectory__SearchIcon" />}
          />
          {toolbarExtra ? (
            <div className="TestDirectory__MyTestsHeaderSort">
              {toolbarExtra}
            </div>
          ) : null}
        </div>
      </div>

      <div className="TestDirectory__MyTestsBody">
        {loading ? (
          <div className="TestDirectory__Loading" aria-busy="true">
            <div className="TestDirectory__Skeleton ActiveloadingAnimation" />
          </div>
        ) : (
          <StyledScrollbar
            noScrollX
            autoHide
            className="TestDirectory__MyTestsScrollbar"
          >
            {customerTests.length ? (
              <div className="TestDirectory__TestLevelGroups">
                {groupedTests.map(({ key: levelKey, label, tests }) => (
                  <section
                    key={levelKey || "my-tests"}
                    className="TestDirectory__TestLevelSection"
                    aria-label={label}
                  >
                    <h4 className="TestDirectory__TestLevelHeader">{label}</h4>
                    <ul className="TestDirectory__MyTestsChipList">
                      {tests.map((test, index) => {
                        const name = getTestName(test);
                        if (!name) return null;
                        return (
                          <li key={`${name}-${index}`} className="TestDirectory__MyTestChip">
                            <StyledTooltip
                              title={buildTestTooltip(test)}
                              placement="top"
                            >
                              <span className="TestDirectory__MyTestChipLabel">
                                <Highlighter
                                  highlightClassName="searchHighlight"
                                  searchWords={[searched]}
                                  autoEscape
                                  textToHighlight={name}
                                />
                              </span>
                            </StyledTooltip>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            ) : (
              <Empty
                className="TestDirectory__CompactEmpty"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No tests found"
              />
            )}
          </StyledScrollbar>
        )}
      </div>
    </section>
  );
}

/**
 * Unlock More Tests — fills section height; search is in the Test Requests toolbar.
 */
export default function TestsBrowse({
  directoryTests = [],
  requestedNames = [],
  unlockSearch = "",
  loadingUnlock = false,
  onUnlockSubmitted,
}) {
  return (
    <div className="TestDirectory__UnlockWorkspace">
      <UnlockTestsPanel
        directoryTests={directoryTests}
        requestedNames={requestedNames}
        search={unlockSearch}
        loading={loadingUnlock}
        onSubmitted={onUnlockSubmitted}
      />
    </div>
  );
}
